/* ==========================================================
   Match-3 Product Game — Ultimate Edition
   - Drag-to-swap (pointer events: mouse + touch)
   - Physics-feel gravity: staggered fall speed by distance,
     landing squash/stretch, punchy pop-burst on match
   - Floating "+N" score popups
   - Built-in tutorial overlay (first-visit + reopenable)
   - Unlimited moves; win when target score is reached
   - Lead capture (name/phone/email) -> Google Apps Script
   ========================================================== */

(function () {
  const ROOT_SELECTOR = '[data-match3-root]';
  const SWAP_MS = 200;
  const POP_MS = 220;
  const BASE_FALL_MS = 180;
  const FALL_MS_PER_ROW = 55;
  const DRAG_THRESHOLD_RATIO = 0.32;

  function initGame(root) {
    const CACHE_VERSION = root.dataset.cacheVersion || 'r1';
    const STORAGE_KEY = 'match3_claimed_' + CACHE_VERSION;
    const SESSION_KEY = 'match3_session_' + CACHE_VERSION;
    const TUTORIAL_KEY = 'match3_tutorial_seen_' + CACHE_VERSION;

    const GRID_SIZE = 6;
    const TARGET_SCORE = parseInt(root.dataset.targetScore, 10) || 500;
    const POINTS_PER_TILE = parseInt(root.dataset.pointsPerTile, 10) || 10;
    const APPS_SCRIPT_URL = root.dataset.appsScriptUrl || '';
    const DISCOUNT_CODE = root.dataset.discountCode || '';

    let PRODUCTS = [];
    try {
      PRODUCTS = JSON.parse(root.dataset.products || '[]');
    } catch (e) {
      PRODUCTS = [];
    }
    if (PRODUCTS.length < 3) {
      console.error('Match3: need at least 3 product blocks configured.');
      return;
    }

    let sessionId = sessionStorage.getItem(SESSION_KEY);
    if (!sessionId) {
      sessionId = 'sid_' + Date.now() + '_' + Math.random().toString(36).slice(2, 10);
      sessionStorage.setItem(SESSION_KEY, sessionId);
    }

    const boardEl = root.querySelector('[data-board]');
    const scoreEl = root.querySelector('[data-score]');
    const targetEl = root.querySelector('[data-target]');
    const progressBarEl = root.querySelector('[data-progress-bar]');
    const startScreen = root.querySelector('[data-start-screen]');
    const gameScreen = root.querySelector('[data-game-screen]');
    const winScreen = root.querySelector('[data-win-screen]');
    const claimForm = root.querySelector('[data-claim-form]');
    const codeReveal = root.querySelector('[data-code-reveal]');
    const codeText = root.querySelector('[data-code-text]');
    const startBtn = root.querySelector('[data-start-btn]');
    const restartBtn = root.querySelector('[data-restart-btn]');
    const floatLayer = root.querySelector('[data-float-layer]');

    // Tutorial elements
    const tutorialModal = root.querySelector('[data-tutorial-modal]');
    const tutorialTrack = root.querySelector('[data-tutorial-track]');
    const tutorialSteps = Array.from(root.querySelectorAll('[data-tutorial-step]'));
    const tutorialDots = Array.from(root.querySelectorAll('[data-tutorial-dot]'));
    const tutorialNextBtn = root.querySelector('[data-tutorial-next]');
    const tutorialPrevBtn = root.querySelector('[data-tutorial-prev]');
    const tutorialSkipBtn = root.querySelector('[data-tutorial-skip]');
    const tutorialCloseBtn = root.querySelector('[data-tutorial-close]');
    const tutorialBackdrop = root.querySelector('[data-tutorial-backdrop]');
    const howToPlayBtns = Array.from(root.querySelectorAll('[data-howtoplay-btn]'));
    const demoImgs = Array.from(root.querySelectorAll('[data-demo-img]'));

    boardEl.style.setProperty('--grid-size', GRID_SIZE);
    targetEl && (targetEl.textContent = TARGET_SCORE);

    // ---- Tile model ----
    let tiles = [];
    let tileIdCounter = 0;
    let score = 0;
    let isAnimating = false;
    let hasWon = false;

    function randomProductIndex() {
      return Math.floor(Math.random() * PRODUCTS.length);
    }

    function cellPct(n) {
      return (n / GRID_SIZE) * 100 + '%';
    }

    function gridFromTiles() {
      const grid = Array.from({ length: GRID_SIZE }, () => Array(GRID_SIZE).fill(null));
      tiles.forEach((t) => { grid[t.row][t.col] = t; });
      return grid;
    }

    function setTransitionDuration(el, ms, easing) {
      el.style.transition = `left ${ms}ms ${easing}, top ${ms}ms ${easing}`;
    }

    function positionTile(tile, animate, ms, easing) {
      const el = tile.el;
      if (!animate) {
        el.style.transition = 'none';
        el.style.left = cellPct(tile.col);
        el.style.top = cellPct(tile.row);
        void el.offsetWidth;
        el.style.transition = '';
      } else {
        setTransitionDuration(el, ms || SWAP_MS, easing || 'cubic-bezier(.34,1.4,.64,1)');
        el.style.left = cellPct(tile.col);
        el.style.top = cellPct(tile.row);
      }
    }

    function makeTileEl(tile) {
      const el = document.createElement('div');
      el.className = 'm3-tile';
      el.dataset.id = tile.id;
      el.style.touchAction = 'none';
      const inner = document.createElement('div');
      inner.className = 'm3-tile-inner';
      const img = document.createElement('img');
      img.src = PRODUCTS[tile.type].image;
      img.alt = PRODUCTS[tile.type].name || '';
      img.draggable = false;
      inner.appendChild(img);
      el.appendChild(inner);
      attachDragHandlers(el, tile);
      return el;
    }

    // ---- Initial board (avoid pre-existing matches) ----
    function createInitialBoard() {
      boardEl.innerHTML = '';
      tiles = [];
      const grid = Array.from({ length: GRID_SIZE }, () => Array(GRID_SIZE).fill(-1));
      for (let r = 0; r < GRID_SIZE; r++) {
        for (let c = 0; c < GRID_SIZE; c++) {
          let type;
          do {
            type = randomProductIndex();
          } while (
            (c >= 2 && grid[r][c - 1] === type && grid[r][c - 2] === type) ||
            (r >= 2 && grid[r - 1][c] === type && grid[r - 2][c] === type)
          );
          grid[r][c] = type;
          const tile = { id: tileIdCounter++, type: type, row: r, col: c };
          tile.el = makeTileEl(tile);
          positionTile(tile, false);
          boardEl.appendChild(tile.el);
          tiles.push(tile);
        }
      }
    }

    function findMatches(grid) {
      const matched = Array.from({ length: GRID_SIZE }, () => Array(GRID_SIZE).fill(false));
      let found = false;

      for (let r = 0; r < GRID_SIZE; r++) {
        let runStart = 0;
        for (let c = 1; c <= GRID_SIZE; c++) {
          const same = c < GRID_SIZE && grid[r][c] && grid[r][c - 1] && grid[r][c].type === grid[r][c - 1].type;
          if (!same) {
            if (c - runStart >= 3) { for (let k = runStart; k < c; k++) { matched[r][k] = true; found = true; } }
            runStart = c;
          }
        }
      }
      for (let c = 0; c < GRID_SIZE; c++) {
        let runStart = 0;
        for (let r = 1; r <= GRID_SIZE; r++) {
          const same = r < GRID_SIZE && grid[r][c] && grid[r - 1][c] && grid[r][c].type === grid[r - 1][c].type;
          if (!same) {
            if (r - runStart >= 3) { for (let k = runStart; k < r; k++) { matched[k][c] = true; found = true; } }
            runStart = r;
          }
        }
      }
      return { matched, found };
    }

    function updateScore(matchedCount, atRow, atCol) {
      const gained = matchedCount * POINTS_PER_TILE;
      score += gained;
      scoreEl && (scoreEl.textContent = score);
      const pct = Math.min(100, Math.round((score / TARGET_SCORE) * 100));
      progressBarEl && (progressBarEl.style.width = pct + '%');
      showFloatingScore(gained, atRow, atCol);
      if (score >= TARGET_SCORE && !hasWon) {
        hasWon = true;
        setTimeout(showWinScreen, 500);
      }
    }

    function showFloatingScore(amount, row, col) {
      if (!floatLayer) return;
      const span = document.createElement('span');
      span.className = 'm3-float-score';
      span.textContent = '+' + amount;
      span.style.left = cellPct(col + 0.5);
      span.style.top = cellPct(row + 0.5);
      floatLayer.appendChild(span);
      setTimeout(function () { span.remove(); }, 700);
    }

    // ---- Pop matched tiles, then gravity-fill ----
    function resolveMatchesLoop() {
      const grid = gridFromTiles();
      const { matched, found } = findMatches(grid);

      if (!found) {
        isAnimating = false;
        return;
      }

      let matchedCount = 0;
      let sumRow = 0, sumCol = 0;
      const matchedTiles = [];
      for (let r = 0; r < GRID_SIZE; r++) {
        for (let c = 0; c < GRID_SIZE; c++) {
          if (matched[r][c] && grid[r][c]) {
            matchedTiles.push(grid[r][c]);
            matchedCount++;
            sumRow += r; sumCol += c;
          }
        }
      }
      const avgRow = sumRow / matchedCount;
      const avgCol = sumCol / matchedCount;

      matchedTiles.forEach((t, i) => {
        t.el.style.transitionDelay = (i % 3) * 15 + 'ms';
        t.el.classList.add('m3-tile-matched');
      });

      setTimeout(function () {
        matchedTiles.forEach((t) => t.el.remove());
        const matchedIds = new Set(matchedTiles.map((t) => t.id));
        tiles = tiles.filter((t) => !matchedIds.has(t.id));

        updateScore(matchedCount, avgRow, avgCol);

        let maxFallMs = BASE_FALL_MS;

        for (let c = 0; c < GRID_SIZE; c++) {
          const colTiles = tiles.filter((t) => t.col === c).sort((a, b) => b.row - a.row);
          let newRow = GRID_SIZE - 1;
          colTiles.forEach((t) => {
            const distance = Math.abs(newRow - t.row);
            if (distance > 0) {
              const ms = BASE_FALL_MS + distance * FALL_MS_PER_ROW;
              maxFallMs = Math.max(maxFallMs, ms);
              t.row = newRow;
              positionTile(t, true, ms, 'cubic-bezier(.5,0,.75,0)');
              markLandingSquash(t, ms);
            }
            newRow--;
          });

          const missing = newRow + 1;
          for (let i = newRow; i >= 0; i--) {
            const type = randomProductIndex();
            const tile = { id: tileIdCounter++, type: type, row: i, col: c };
            tile.el = makeTileEl(tile);
            const startRow = i - missing;
            tile.row = startRow;
            positionTile(tile, false);
            boardEl.appendChild(tile.el);
            tiles.push(tile);
            const distance = Math.abs(i - startRow);
            const ms = BASE_FALL_MS + distance * FALL_MS_PER_ROW;
            maxFallMs = Math.max(maxFallMs, ms);
            (function (t2, finalRow, duration) {
              requestAnimationFrame(function () {
                t2.row = finalRow;
                positionTile(t2, true, duration, 'cubic-bezier(.5,0,.75,0)');
                markLandingSquash(t2, duration);
              });
            })(tile, i, ms);
          }
        }

        setTimeout(resolveMatchesLoop, maxFallMs + 60);
      }, POP_MS);
    }

    function markLandingSquash(tile, ms) {
      const handler = function () {
        tile.el.removeEventListener('transitionend', handler);
        tile.el.classList.remove('m3-landing');
        void tile.el.offsetWidth;
        tile.el.classList.add('m3-landing');
        setTimeout(function () { tile.el.classList.remove('m3-landing'); }, 220);
      };
      tile.el.addEventListener('transitionend', handler);
    }

    // ---- Drag-to-swap ----
    function getNeighbor(tile, dir) {
      let r = tile.row, c = tile.col;
      if (dir === 'left') c -= 1;
      else if (dir === 'right') c += 1;
      else if (dir === 'up') r -= 1;
      else if (dir === 'down') r += 1;
      if (r < 0 || r >= GRID_SIZE || c < 0 || c >= GRID_SIZE) return null;
      return tiles.find((t) => t.row === r && t.col === c) || null;
    }

    function attemptSwap(a, b) {
      if (isAnimating || hasWon) return;
      isAnimating = true;

      const aRow = a.row, aCol = a.col;
      a.row = b.row; a.col = b.col;
      b.row = aRow; b.col = aCol;
      positionTile(a, true, SWAP_MS, 'cubic-bezier(.34,1.4,.64,1)');
      positionTile(b, true, SWAP_MS, 'cubic-bezier(.34,1.4,.64,1)');

      setTimeout(function () {
        const grid = gridFromTiles();
        const { found } = findMatches(grid);
        if (!found) {
          const tmpRow = a.row, tmpCol = a.col;
          a.row = b.row; a.col = b.col;
          b.row = tmpRow; b.col = tmpCol;
          positionTile(a, true, SWAP_MS, 'cubic-bezier(.34,1.4,.64,1)');
          positionTile(b, true, SWAP_MS, 'cubic-bezier(.34,1.4,.64,1)');
          setTimeout(function () { isAnimating = false; }, SWAP_MS);
        } else {
          resolveMatchesLoop();
        }
      }, SWAP_MS);
    }

    function attachDragHandlers(el, tile) {
      let drag = null;

      el.addEventListener('pointerdown', function (e) {
        if (isAnimating || hasWon) return;
        el.setPointerCapture(e.pointerId);
        drag = { pointerId: e.pointerId, startX: e.clientX, startY: e.clientY, triggered: false };
        el.classList.add('m3-dragging');
      });

      el.addEventListener('pointermove', function (e) {
        if (!drag || drag.pointerId !== e.pointerId || drag.triggered) return;
        const dx = e.clientX - drag.startX;
        const dy = e.clientY - drag.startY;
        const rect = el.getBoundingClientRect();
        const maxOffset = rect.width * 0.4;
        const clampedX = Math.max(-maxOffset, Math.min(maxOffset, dx));
        const clampedY = Math.max(-maxOffset, Math.min(maxOffset, dy));

        if (Math.abs(dx) > Math.abs(dy)) {
          el.style.transform = `translate(${clampedX}px,0)`;
        } else {
          el.style.transform = `translate(0,${clampedY}px)`;
        }

        const threshold = rect.width * DRAG_THRESHOLD_RATIO;
        if (Math.abs(dx) > threshold || Math.abs(dy) > threshold) {
          drag.triggered = true;
          let dir;
          if (Math.abs(dx) > Math.abs(dy)) dir = dx > 0 ? 'right' : 'left';
          else dir = dy > 0 ? 'down' : 'up';

          el.style.transition = 'transform 0.12s ease';
          el.style.transform = 'translate(0,0)';
          setTimeout(function () { el.style.transition = ''; }, 130);

          const neighbor = getNeighbor(tile, dir);
          if (neighbor) attemptSwap(tile, neighbor);
          endDrag(e);
        }
      });

      function snapBack() {
        el.style.transition = 'transform 0.18s cubic-bezier(.34,1.4,.64,1)';
        el.style.transform = 'translate(0,0)';
        setTimeout(function () { el.style.transition = ''; }, 190);
      }

      function endDrag(e) {
        el.classList.remove('m3-dragging');
        try { el.releasePointerCapture(e.pointerId); } catch (err) {}
        drag = null;
      }

      el.addEventListener('pointerup', function (e) {
        if (!drag) return;
        if (!drag.triggered) snapBack();
        endDrag(e);
      });

      el.addEventListener('pointercancel', function (e) {
        if (!drag) return;
        if (!drag.triggered) snapBack();
        endDrag(e);
      });
    }

    // ---- Screens ----
    function showScreen(el) {
      [startScreen, gameScreen, winScreen].forEach((s) => s && s.classList.remove('m3-active'));
      el && el.classList.add('m3-active');
    }

    function showWinScreen() {
      showScreen(winScreen);
      trackEvent('game_won');
    }

    function startGame() {
      score = 0;
      hasWon = false;
      isAnimating = false;
      scoreEl && (scoreEl.textContent = '0');
      progressBarEl && (progressBarEl.style.width = '0%');
      createInitialBoard();
      showScreen(gameScreen);
      trackEvent('game_started');
    }

    startBtn && startBtn.addEventListener('click', function () {
      if (localStorage.getItem(TUTORIAL_KEY)) {
        startGame();
      } else {
        openTutorial(true);
      }
    });
    restartBtn && restartBtn.addEventListener('click', startGame);

    // ---- Tutorial ----
    let tutorialIndex = 0;
    let tutorialStartsGame = false;

    // Populate demo tiles with real product thumbnails (falls back gracefully if missing)
    demoImgs.forEach((img) => {
      const idx = parseInt(img.dataset.demoImg, 10);
      const product = PRODUCTS[idx] || PRODUCTS[0];
      if (product && product.image) img.src = product.image;
    });

    function applyTutorialStep() {
      tutorialSteps.forEach((s, i) => s.classList.toggle('m3-active', i === tutorialIndex));
      tutorialDots.forEach((d, i) => d.classList.toggle('m3-active', i === tutorialIndex));
      if (tutorialPrevBtn) tutorialPrevBtn.style.visibility = tutorialIndex === 0 ? 'hidden' : 'visible';
      if (tutorialNextBtn) {
        tutorialNextBtn.textContent = tutorialIndex === tutorialSteps.length - 1
          ? (tutorialNextBtn.dataset.finalLabel || "Let's play!")
          : (tutorialNextBtn.dataset.nextLabel || 'Next');
      }
    }

    function goToTutorialStep(newIndex, direction) {
      newIndex = Math.max(0, Math.min(tutorialSteps.length - 1, newIndex));
      if (newIndex === tutorialIndex || !tutorialTrack) {
        tutorialIndex = newIndex;
        applyTutorialStep();
        return;
      }
      const outX = direction === 'prev' ? '14px' : '-14px';
      tutorialTrack.style.setProperty('--m3-step-out-x', outX);
      tutorialTrack.classList.add('m3-step-out');
      setTimeout(function () {
        tutorialIndex = newIndex;
        applyTutorialStep();
        tutorialTrack.classList.remove('m3-step-out');
      }, 160);
    }

    function renderTutorialStep() {
      applyTutorialStep();
    }

    function openTutorial(startsGameAfter) {
      tutorialIndex = 0;
      tutorialStartsGame = !!startsGameAfter;
      applyTutorialStep();
      tutorialModal && tutorialModal.classList.add('m3-active');
    }

    function closeTutorial() {
      tutorialModal && tutorialModal.classList.remove('m3-active');
      localStorage.setItem(TUTORIAL_KEY, '1');
      if (tutorialStartsGame) startGame();
      tutorialStartsGame = false;
    }

    tutorialNextBtn && tutorialNextBtn.addEventListener('click', function () {
      if (tutorialIndex < tutorialSteps.length - 1) {
        goToTutorialStep(tutorialIndex + 1, 'next');
      } else {
        closeTutorial();
      }
    });
    tutorialPrevBtn && tutorialPrevBtn.addEventListener('click', function () {
      if (tutorialIndex > 0) goToTutorialStep(tutorialIndex - 1, 'prev');
    });
    tutorialDots.forEach((dot, i) => {
      dot.addEventListener('click', function () {
        goToTutorialStep(i, i > tutorialIndex ? 'next' : 'prev');
      });
    });
    tutorialSkipBtn && tutorialSkipBtn.addEventListener('click', closeTutorial);
    tutorialCloseBtn && tutorialCloseBtn.addEventListener('click', closeTutorial);
    tutorialBackdrop && tutorialBackdrop.addEventListener('click', closeTutorial);
    howToPlayBtns.forEach((btn) => btn.addEventListener('click', function () { openTutorial(false); }));

    // Swipe left/right on the tutorial panel to move between steps
    if (tutorialTrack) {
      let swipe = null;
      tutorialTrack.addEventListener('pointerdown', function (e) {
        swipe = { startX: e.clientX, startY: e.clientY };
      });
      tutorialTrack.addEventListener('pointerup', function (e) {
        if (!swipe) return;
        const dx = e.clientX - swipe.startX;
        const dy = e.clientY - swipe.startY;
        swipe = null;
        if (Math.abs(dx) < 40 || Math.abs(dx) < Math.abs(dy)) return;
        if (dx < 0 && tutorialIndex < tutorialSteps.length - 1) {
          goToTutorialStep(tutorialIndex + 1, 'next');
        } else if (dx > 0 && tutorialIndex > 0) {
          goToTutorialStep(tutorialIndex - 1, 'prev');
        }
      });
    }

    // ---- Lead capture / claim ----
    if (claimForm) {
      claimForm.addEventListener('submit', function (e) {
        e.preventDefault();
        const name = claimForm.querySelector('[name="name"]').value.trim();
        const phone = claimForm.querySelector('[name="phone"]').value.trim();
        const email = claimForm.querySelector('[name="email"]').value.trim();
        if (!name || !phone) return;

        submitLead({ name, phone, email }).finally(function () {
          claimForm.style.display = 'none';
          codeReveal && codeReveal.classList.add('m3-active');
          codeText && (codeText.textContent = DISCOUNT_CODE);
          localStorage.setItem(STORAGE_KEY, '1');
        });
      });
    }

    function trackEvent(eventName) {
      if (!APPS_SCRIPT_URL) return;
      fetch(APPS_SCRIPT_URL, {
        method: 'POST',
        mode: 'no-cors',
        headers: { 'Content-Type': 'text/plain;charset=utf-8' },
        body: JSON.stringify({
          type: 'event', event: eventName, sessionId: sessionId,
          score: score, timestamp: new Date().toISOString()
        })
      }).catch(function () {});
    }

    function submitLead(lead) {
      if (!APPS_SCRIPT_URL) return Promise.resolve();
      return fetch(APPS_SCRIPT_URL, {
        method: 'POST',
        mode: 'no-cors',
        headers: { 'Content-Type': 'text/plain;charset=utf-8' },
        body: JSON.stringify({
          type: 'lead', sessionId: sessionId, name: lead.name, phone: lead.phone,
          email: lead.email, score: score, timestamp: new Date().toISOString()
        })
      }).catch(function () {});
    }

    showScreen(startScreen);
    if (!localStorage.getItem(TUTORIAL_KEY)) {
      openTutorial(false);
    }
  }

  function init() {
    document.querySelectorAll(ROOT_SELECTOR).forEach(initGame);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();